import React from "react";

type Block =
  | { type: "p"; lead?: string; text: string }
  | { type: "list"; items: string[] }
  | { type: "plainList"; items: string[] }
  | { type: "heading"; text: string }
  | { type: "address"; lines: string[] };

interface Subsection {
  title: string;
  blocks: Block[];
}

interface Section {
  id: string;
  title: string;
  blocks: Block[];
  subsections?: Subsection[];
}

const TITLE = "REALESY TERMS OF SERVICE";
const intro: string[] = [
  "These Terms of Service (“Terms”) constitute a legally binding agreement between RealEsy, LLC (“RealEsy,” “we,” “us,” or “our”) and the individual or legal entity that accesses, registers for, purchases, or uses the RealEsy website, mobile applications, software platform, products, features, integrations, and related services (collectively, the “Services”).",
  "By creating an Account, clicking “I Agree,” accessing or using the Services, purchasing a Subscription Plan or Add-On, or otherwise affirmatively indicating acceptance of these Terms, you acknowledge that you have read, understood, and agree to be bound by these Terms and the RealEsy Privacy Policy.",
  "If you accept these Terms on behalf of a company, brokerage, vendor, office, organization, or other legal entity, you represent and warrant that you have authority to bind that entity to these Terms. In such case, “Customer,” “you,” and “your” refer to that entity and, where applicable, its Authorized Users.",
  "THESE TERMS CONTAIN AN AGREEMENT TO ARBITRATE CERTAIN DISPUTES, AN INDIVIDUAL-ACTION AND CLASS-ACTION WAIVER, AND A JURY-TRIAL WAIVER. PLEASE REVIEW SECTION 35 CAREFULLY.",
  "If you do not agree to these Terms, you may not access or use the Services.",
];

const sections: Section[] = [
  {
    id: "definitions",
    title: "1. Definitions",
    blocks: [
      { type: "p", text: "For purposes of these Terms:" },
      {
        type: "p",
        lead: "“Account”",
        text: "means an account created or maintained to access the Services.",
      },
      {
        type: "p",
        lead: "“Add-On”",
        text: "means optional functionality, capacity, integration, service, module, or other feature purchased in addition to or in connection with a Subscription Plan.",
      },
      {
        type: "p",
        lead: "“Authorized User”",
        text: "means an individual whom a Customer authorizes to access or use the Services under its Account.",
      },
      {
        type: "p",
        lead: "“Brokerage”",
        text: "means a real estate brokerage, office, organization, or related business using the Services.",
      },
      {
        type: "p",
        lead: "“Customer”",
        text: "means the individual or legal entity that registers for, purchases, or otherwise receives access to the Services.",
      },
      {
        type: "p",
        lead: "“Customer Content”",
        text: "means information, data, photographs, videos, documents, artwork, designs, logos, messages, files, records, instructions, and other materials submitted to, stored in, transmitted through, or created by a Customer or Authorized User through the Services.",
      },
      {
        type: "p",
        lead: "“End Customer”",
        text: "means an individual or entity that purchases, requests, receives, or is the intended recipient of products or services offered or provided by a Vendor or Service Provider through transactions or workflows managed using the Services.",
      },
      {
        type: "p",
        lead: "“Order”",
        text: "means a transaction, request, quote, purchase, shipment, or other order created or managed through the Services.",
      },
      {
        type: "p",
        lead: "“RealEsy Intellectual Property”",
        text: "means the Services and all software, source code, object code, applications, architecture, databases, database structures, APIs, proprietary integrations, workflows, processes, methods, functionality, user interfaces, user experiences, layouts, screen designs, navigation structures, reports, templates, forms, documentation, graphics, icons, branding, trade names, logos, training materials, AI-related implementations, enhancements, modifications, derivative works, and other intellectual property owned by or licensed to RealEsy, excluding Customer Content and third-party materials.",
      },
      {
        type: "p",
        lead: "“Service Order”",
        text: "means a request for installation, removal, repair, maintenance, delivery, pickup, or another service performed by a Vendor or Service Provider.",
      },
      {
        type: "p",
        lead: "“Service Provider”",
        text: "means an independent business or individual providing installation, removal, repair, maintenance, delivery, pickup, or other services.",
      },
      {
        type: "p",
        lead: "“Subscription Plan”",
        text: "means a paid or unpaid plan through which access to specified Services is provided.",
      },
      {
        type: "p",
        lead: "“Vendor”",
        text: "means an independent business offering, selling, supplying, or providing products or services through or using the RealEsy platform.",
      },
    ],
  },
  {
    id: "description-of-services",
    title: "2. Description of the Services",
    blocks: [
      {
        type: "p",
        text: "RealEsy is a cloud-based business software platform designed to facilitate and manage business operations among Vendors, Brokerages, offices, real estate agents, installers, service technicians, warehouse personnel, End Customers, and other authorized users.",
      },
      {
        type: "p",
        text: "Depending on the Customer’s Subscription Plan, Add-Ons, configuration, permissions, and available integrations, the Services may include:",
      },
      {
        type: "list",
        items: [
          "marketplace functionality;",
          "customer relationship management;",
          "Order management;",
          "Service Orders;",
          "installation and removal scheduling;",
          "inventory management;",
          "warehouse management;",
          "brokerage, office, and agent management;",
          "quotes and approvals;",
          "invoicing and payment integrations;",
          "shipping functionality;",
          "proof images;",
          "GPS, location, mapping, and routing functionality;",
          "reporting and analytics;",
          "messaging and operational communications;",
          "design and proof-management tools;",
          "artificial intelligence functionality; and",
          "other related business-management functionality.",
        ],
      },
      {
        type: "p",
        text: "RealEsy provides technology and workflow-management functionality.",
      },
      {
        type: "p",
        text: "Except where expressly stated in a separate written agreement, RealEsy does not itself provide the underlying products, installations, removals, repairs, brokerage services, transportation, shipping, payment processing, or other services offered by independent third parties through or in connection with the platform.",
      },
      {
        type: "p",
        text: "RealEsy may add, modify, improve, replace, restrict, or discontinue functionality from time to time in accordance with these Terms.",
      },
    ],
  },
  {
    id: "eligibility-and-authority",
    title: "3. Eligibility and Authority",
    blocks: [
      {
        type: "p",
        text: "You must be at least eighteen (18) years of age and legally capable of entering into a binding agreement to create an Account or use the Services.",
      },
      {
        type: "p",
        text: "By accessing or using the Services, you represent and warrant that you satisfy these requirements and are not prohibited from using the Services under applicable law.",
      },
      {
        type: "p",
        text: "If you use the Services on behalf of an organization, you represent and warrant that you have authority to act on behalf of and bind that organization to these Terms.",
      },
      {
        type: "p",
        text: "Customers are responsible for determining whether their Authorized Users, employees, contractors, Vendors, Service Providers, or other personnel possess any licenses, certifications, permits, insurance, authorizations, or professional qualifications required for their respective activities.",
      },
      {
        type: "p",
        text: "Except where expressly stated otherwise, RealEsy does not verify or guarantee the professional qualifications, licensing, insurance coverage, authority, background, or legal compliance of Vendors, Brokerages, agents, installers, Service Providers, or other users.",
      },
    ],
  },
  {
    id: "accounts-and-security",
    title: "4. Accounts and Account Security",
    blocks: [
      {
        type: "p",
        text: "Customers and Authorized Users must provide accurate, current, and complete information when creating and maintaining an Account.",
      },
      { type: "p", text: "Customers and Authorized Users are responsible for:" },
      {
        type: "list",
        items: [
          "maintaining the confidentiality of login credentials;",
          "maintaining accurate Account and contact information;",
          "restricting unauthorized access to their Accounts;",
          "using reasonable security practices;",
          "promptly notifying RealEsy of suspected unauthorized access or security incidents; and",
          "activities conducted through their Accounts to the extent permitted by applicable law.",
        ],
      },
      {
        type: "p",
        text: "Users may not share credentials with unauthorized persons, impersonate another person or organization, or access an Account without authorization.",
      },
      {
        type: "p",
        text: "RealEsy may require identity, email, telephone, organization, or other verification where reasonably necessary for security, fraud prevention, legal compliance, or platform administration.",
      },
    ],
  },
  {
    id: "organizations-and-permissions",
    title: "5. Organizations, Authorized Users and Permissions",
    blocks: [
      {
        type: "p",
        text: "Customers may authorize employees, agents, managers, administrators, contractors, installers, warehouse personnel, designers, Service Providers, or other individuals to use the Services on their behalf.",
      },
      { type: "p", text: "Customers are solely responsible for:" },
      {
        type: "list",
        items: [
          "selecting their Authorized Users;",
          "assigning appropriate roles and permissions;",
          "granting, modifying, and revoking access;",
          "supervising use of the Services by their Authorized Users; and",
          "ensuring their Authorized Users comply with these Terms.",
        ],
      },
      {
        type: "p",
        text: "RealEsy provides role-based access controls as a software feature but is not responsible for a Customer’s selection, configuration, or administration of user permissions.",
      },
      {
        type: "p",
        text: "Actions taken through an authenticated Account by an Authorized User acting within access granted by the Customer may be treated as actions authorized by and attributable to the Customer.",
      },
      {
        type: "p",
        text: "Customer is responsible for promptly disabling access when an Authorized User is no longer authorized to act on Customer’s behalf.",
      },
    ],
  },
  {
    id: "limited-license",
    title: "6. Limited License to Use the Services",
    blocks: [
      {
        type: "p",
        text: "Subject to these Terms and payment of all applicable fees, RealEsy grants Customer a limited, non-exclusive, non-transferable, non-sublicensable, revocable right during the applicable subscription or authorized access period to access and use the Services solely for Customer’s legitimate internal business purposes.",
      },
      {
        type: "p",
        text: "No ownership interest in the Services or RealEsy Intellectual Property is transferred to Customer or any Authorized User.",
      },
      {
        type: "p",
        text: "All rights not expressly granted under these Terms are reserved by RealEsy and its licensors.",
      },
    ],
  },
  {
    id: "marketplace-relationships",
    title: "7. Marketplace and Independent Business Relationships",
    blocks: [
      {
        type: "p",
        text: "RealEsy provides technology that enables independent businesses and users to interact and manage transactions and workflows.",
      },
      {
        type: "p",
        text: "Unless RealEsy expressly agrees otherwise in a separate written agreement, RealEsy:",
      },
      {
        type: "list",
        items: [
          "is not the seller or manufacturer of products offered by independent Vendors;",
          "does not perform installations, removals, repairs, maintenance, deliveries, pickups, or other field services offered by independent Service Providers;",
          "does not provide real estate brokerage services;",
          "does not employ or control independent Vendors, Brokerages, real estate agents, installers, Service Providers, or their personnel;",
          "does not supervise the manner in which independent Vendors or Service Providers perform their work; and",
          "is not a party to the underlying transaction between an independent Vendor or Service Provider and an End Customer.",
        ],
      },
      {
        type: "p",
        text: "Vendors, Brokerages, agents, installers, Service Providers, End Customers, and other users operate independently and are responsible for their own acts, omissions, products, services, personnel, legal compliance, transactions, representations, and business relationships.",
      },
      {
        type: "p",
        text: "Nothing in these Terms creates an employment, agency, partnership, joint venture, franchise, fiduciary, or similar relationship between RealEsy and any independent Vendor, Brokerage, agent, installer, Service Provider, End Customer, or other user.",
      },
    ],
  },
  {
    id: "realesy-as-vendor",
    title: "8. RealEsy or Affiliates Acting as a Vendor or Service Provider",
    blocks: [
      {
        type: "p",
        text: "RealEsy or an entity affiliated with RealEsy may, from time to time, participate on the platform in the capacity of a Vendor or Service Provider.",
      },
      {
        type: "p",
        text: "Where RealEsy or an affiliated entity expressly acts in such capacity with respect to a particular transaction, product, or service, the obligations applicable to a Vendor or Service Provider with respect to that transaction will apply to RealEsy or the applicable affiliate in that capacity.",
      },
      {
        type: "p",
        text: "For clarity, provisions of these Terms disclaiming RealEsy’s responsibility for products or services provided by independent Vendors or Service Providers do not excuse RealEsy or an applicable affiliate from obligations expressly undertaken by it in a separately identified Vendor or Service Provider capacity.",
      },
      {
        type: "p",
        text: "RealEsy’s role as operator of the software platform remains legally and operationally distinct from any separately identified Vendor or Service Provider role.",
      },
    ],
  },
  {
    id: "vendor-responsibility",
    title: "9. Vendor and Service Provider Responsibility",
    blocks: [
      {
        type: "p",
        text: "Each Vendor and Service Provider is solely responsible for its products, services, business operations, personnel, and obligations, including:",
      },
      {
        type: "list",
        items: [
          "product descriptions, specifications, quality, accuracy, condition, safety, and availability;",
          "installation, removal, repair, maintenance, delivery, pickup, and other services;",
          "scheduling and communications with End Customers;",
          "workmanship and completion of Service Orders;",
          "employees, installers, subcontractors, agents, and other personnel;",
          "licenses, permits, insurance, certifications, and regulatory requirements;",
          "compliance with applicable safety and professional standards;",
          "warranties and guarantees;",
          "customer service;",
          "cancellations, refunds, credits, returns, and disputes; and",
          "claims arising from its products, services, personnel, acts, or omissions.",
        ],
      },
      {
        type: "p",
        text: "RealEsy does not warrant, endorse, guarantee, supervise, inspect, or assume responsibility for the quality, safety, legality, suitability, timeliness, workmanship, or performance of products or services offered or performed by independent Vendors or Service Providers.",
      },
    ],
    subsections: [
      {
        title: "9.1 Insurance Requirements",
        blocks: [
          {
            type: "p",
            text: "RealEsy may require Vendors and Service Providers, as a condition of accessing or continuing to participate in certain portions of the Services, to maintain commercial general liability, professional liability, automobile, workers’ compensation, cyber, or other insurance reasonably appropriate to the products or services they provide.",
          },
          {
            type: "p",
            text: "Where such insurance is required, RealEsy may require evidence of coverage, certificates of insurance, minimum coverage amounts, additional-insured status, or other insurance-related documentation.",
          },
          {
            type: "p",
            text: "Unless RealEsy expressly imposes such a requirement in writing, nothing in these Terms shall be construed as a representation that RealEsy has verified, monitors, or guarantees the insurance coverage of any Vendor or Service Provider.",
          },
        ],
      },
    ],
  },
  {
    id: "orders-and-electronic-actions",
    title: "10. Orders, Service Orders, Quotes, Proofs and Electronic Actions",
    blocks: [
      {
        type: "p",
        text: "The Services may allow users to create, submit, receive, approve, reject, modify, schedule, fulfill, track, or otherwise manage Orders, Service Orders, quotes, proofs, invoices, payments, designs, and related business records.",
      },
      {
        type: "p",
        text: "The parties to an underlying transaction are responsible for verifying all material information before approving, submitting, fulfilling, or relying upon a transaction, including products, quantities, prices, addresses, specifications, schedules, instructions, taxes, shipping information, artwork, designs, proofs, service requirements, and other relevant details.",
      },
      {
        type: "p",
        text: "RealEsy provides workflow and record-management functionality but does not guarantee that information entered, submitted, uploaded, approved, or maintained by users is accurate, complete, authorized, or suitable for its intended purpose.",
      },
    ],
    subsections: [
      {
        title: "10.1 Electronic Records, Approvals and Signatures",
        blocks: [
          {
            type: "p",
            text: "Customer consents to the use of electronic records, communications, approvals, acknowledgments, authorizations, and signatures in connection with the Services.",
          },
          {
            type: "p",
            text: "To the extent permitted by applicable law, actions taken through an authenticated Account, including approving, rejecting, submitting, accepting, or confirming quotes, proofs, Orders, Service Orders, invoices, payments, designs, or other records, may constitute the electronic action, approval, authorization, acknowledgment, or signature of the applicable Customer or Authorized User.",
          },
          {
            type: "p",
            text: "Customer is responsible for ensuring that each Authorized User permitted to take such actions has appropriate authority to do so.",
          },
          {
            type: "p",
            text: "RealEsy may maintain electronic records, timestamps, audit information, status changes, approval records, and other system logs relating to such actions.",
          },
        ],
      },
    ],
  },
  {
    id: "payments",
    title: "11. Payments and Third-Party Payment Processing",
    blocks: [
      {
        type: "p",
        text: "RealEsy may provide integrations that enable Vendors to accept credit card, debit card, ACH, Net 30, deposit, invoice, or other supported payment methods.",
      },
      {
        type: "p",
        text: "Each Vendor connects and maintains its own account with the applicable third-party payment processor.",
      },
      {
        type: "p",
        text: "Unless expressly stated otherwise, payments made by End Customers for Vendor products or services are processed through the Vendor’s connected payment account and are paid directly to the Vendor.",
      },
      {
        type: "p",
        text: "REALESY DOES NOT RECEIVE, HOLD, CONTROL, OR TAKE CUSTODY OF END CUSTOMER FUNDS PROCESSED ON BEHALF OF VENDORS AND IS NOT THE MERCHANT OF RECORD FOR TRANSACTIONS BETWEEN VENDORS AND END CUSTOMERS.",
      },
      {
        type: "p",
        text: "RealEsy is not a bank, financial institution, payment processor, escrow provider, money transmitter, lender, or credit provider.",
      },
      {
        type: "p",
        text: "Vendors are solely responsible for prices, discounts, promotions, payment and credit terms including Net 30, deposits, refunds, credits, cancellations, returns, chargebacks, payment disputes, taxes, tax compliance, and applicable payment-processing requirements.",
      },
      {
        type: "p",
        text: "RealEsy is not responsible for declined transactions, delayed settlements, processor holds or reserves, chargebacks, payment fraud, payment processor outages, processor Account restrictions or suspensions, or other acts or omissions of independent payment providers.",
      },
      {
        type: "p",
        text: "RealEsy currently charges Customers for applicable Subscription Plans, Add-Ons, custom plans, professional services, and other RealEsy services as separately disclosed.",
      },
      {
        type: "p",
        text: "Unless expressly disclosed in applicable pricing terms, RealEsy does not charge a commission merely because a Vendor completes a transaction with an End Customer.",
      },
      {
        type: "p",
        text: "Nothing in these Terms prevents RealEsy from introducing new pricing structures or fees in the future, provided applicable fees are disclosed in accordance with these Terms and applicable law before they become applicable to a Customer.",
      },
    ],
  },
  {
    id: "taxes",
    title: "12. Taxes",
    blocks: [
      {
        type: "p",
        text: "Customers, Vendors, and other parties to transactions are responsible for determining and satisfying their respective tax obligations.",
      },
      {
        type: "p",
        text: "RealEsy may provide integrations with third-party tax-calculation, tax-management, or reporting services.",
      },
      {
        type: "p",
        text: "Such functionality is provided as a convenience and does not constitute tax, accounting, financial, or legal advice.",
      },
      {
        type: "p",
        text: "Users remain responsible for reviewing tax information and complying with applicable tax laws.",
      },
    ],
  },
  {
    id: "subscriptions-and-fees",
    title: "13. Subscription Plans, Add-Ons and Fees",
    blocks: [
      {
        type: "p",
        text: "RealEsy may offer free or paid Subscription Plans, Add-Ons, usage-based services, professional services, and other paid functionality.",
      },
      {
        type: "p",
        text: "Features, usage limits, storage, number of users, locations, integrations, support levels, and other functionality may vary by Subscription Plan or Add-On.",
      },
      {
        type: "p",
        text: "Customers agree to pay all fees associated with the Subscription Plans, Add-Ons, custom plans, and other RealEsy services they purchase or authorize.",
      },
      {
        type: "p",
        text: "Where automatic renewal is enabled and legally permitted, subscriptions may automatically renew according to the disclosed billing cycle unless cancelled in accordance with applicable subscription terms.",
      },
      {
        type: "p",
        text: "RealEsy may modify its Subscription Plans, Add-Ons, pricing, features, usage limits, or billing structure.",
      },
      {
        type: "p",
        text: "Where required by applicable law or contract, RealEsy will provide reasonable advance notice of material pricing changes.",
      },
      {
        type: "p",
        text: "Unless otherwise expressly stated or required by applicable law, fees already paid are non-refundable.",
      },
      {
        type: "p",
        text: "Customers are responsible for applicable taxes, duties, assessments, and governmental charges associated with their purchase of RealEsy Services, excluding taxes imposed on RealEsy’s net income.",
      },
      {
        type: "p",
        text: "Failed, declined, reversed, or overdue payments may result in suspension, restriction, or termination of paid Services.",
      },
    ],
  },
  {
    id: "custom-plans",
    title: "14. Custom Plans, Custom Development and Professional Services",
    blocks: [
      {
        type: "p",
        text: "RealEsy may provide custom Subscription Plans, enterprise arrangements, custom development, integrations, implementation, configuration, migration, consulting, training, onboarding, or other professional services.",
      },
      {
        type: "p",
        text: "Such services may be governed by a separate proposal, quote, statement of work, order form, or written agreement.",
      },
      { type: "p", text: "Unless otherwise expressly agreed in writing:" },
      {
        type: "plainList",
        items: [
          "1. fees for such services are payable to and retained by RealEsy;",
          "2. fees are non-refundable once applicable work has commenced;",
          "3. payment for custom development does not transfer ownership of RealEsy Intellectual Property to Customer;",
          "4. RealEsy retains ownership of its software, source code, frameworks, libraries, architecture, workflows, methods, know-how, tools, APIs, generic functionality, enhancements, and derivative technology; and",
          "5. functionality, improvements, integrations, workflows, reports, automations, or enhancements developed in connection with a Customer project may be incorporated into RealEsy’s generally available Services and may be offered to other customers.",
        ],
      },
      {
        type: "p",
        text: "Any exclusive ownership, exclusivity, source-code ownership, work-made-for-hire treatment, or exclusive-use rights must be expressly stated in a separate written agreement signed by an authorized representative of RealEsy.",
      },
    ],
  },
  {
    id: "customer-content",
    title: "15. Customer Content and Data",
    blocks: [
      {
        type: "p",
        text: "As between RealEsy and Customer, Customer retains its ownership rights in Customer Content, subject to third-party rights and applicable law.",
      },
      {
        type: "p",
        text: "Customer grants RealEsy and its service providers a worldwide, non-exclusive, royalty-free license to host, store, reproduce, process, transmit, display, technically modify, back up, and otherwise use Customer Content as reasonably necessary to:",
      },
      {
        type: "list",
        items: [
          "provide, maintain, secure, and operate the Services;",
          "perform Customer-requested actions;",
          "fulfill Customer requests;",
          "provide technical support;",
          "detect, investigate, and prevent fraud, abuse, and security threats;",
          "troubleshoot and improve reliability and performance;",
          "comply with applicable law; and",
          "exercise RealEsy’s rights and perform its obligations under these Terms.",
        ],
      },
      {
        type: "p",
        text: "Customer represents and warrants that it possesses all rights, permissions, consents, notices, and lawful bases necessary to submit, process, store, share, and use Customer Content through the Services.",
      },
      {
        type: "p",
        text: "Customer is responsible for the legality, accuracy, quality, and appropriateness of Customer Content.",
      },
    ],
    subsections: [
      {
        title: "15.1 Aggregated and De-Identified Information",
        blocks: [
          {
            type: "p",
            text: "RealEsy may create and use aggregated, statistical, or de-identified information derived from use of the Services, provided such information does not reasonably identify Customer or an identifiable individual.",
          },
          {
            type: "p",
            text: "RealEsy may use such aggregated or de-identified information for lawful purposes including analytics, security, benchmarking, research, product development, service improvement, capacity planning, business intelligence, and understanding platform usage and performance.",
          },
          {
            type: "p",
            text: "Nothing in this provision transfers ownership of identifiable Customer Content to RealEsy.",
          },
        ],
      },
    ],
  },
  {
    id: "designs-and-proofs",
    title: "16. Designs, Artwork, Logos and Proofs",
    blocks: [
      {
        type: "p",
        text: "RealEsy may provide tools for creating, uploading, managing, reviewing, sharing, and approving designs, proofs, artwork, logos, trademarks, branding materials, and other content.",
      },
      {
        type: "p",
        text: "RealEsy does not claim ownership of Customer-created or Customer-uploaded artwork, logos, trademarks, branding materials, or design files solely because such materials are uploaded to or processed through the Services.",
      },
      {
        type: "p",
        text: "Customers and Vendors are responsible for ensuring they possess all rights, permissions, licenses, and authorizations necessary to upload, reproduce, modify, distribute, print, approve, or otherwise use such materials.",
      },
      {
        type: "p",
        text: "RealEsy is not responsible for intellectual property disputes between users concerning Customer Content.",
      },
      {
        type: "p",
        text: "Approval of a proof through the Services may constitute Customer’s or an Authorized User’s authorization to proceed with the applicable production, Order, or service, subject to the underlying agreement between the applicable parties.",
      },
    ],
  },
  {
    id: "realesy-intellectual-property",
    title: "17. RealEsy Intellectual Property",
    blocks: [
      {
        type: "p",
        text: "RealEsy Intellectual Property is owned by or licensed to RealEsy and is protected by copyright, trademark, trade secret, patent, and other intellectual property and proprietary-rights laws.",
      },
      { type: "p", text: "RealEsy Intellectual Property includes, without limitation:" },
      {
        type: "list",
        items: [
          "software, source code, object code, and applications;",
          "websites and mobile applications;",
          "architecture and system structure;",
          "databases and database structures;",
          "APIs and proprietary integrations;",
          "workflows, processes, methods, and functionality;",
          "user interfaces and user experiences;",
          "layouts, screen designs, navigation structures, and visual elements;",
          "reports, templates, forms, and documentation;",
          "graphics, icons, branding, trade names, and logos;",
          "training and support materials;",
          "AI-related platform functionality, orchestration, workflows, prompts, tools, and proprietary implementations; and",
          "enhancements, updates, modifications, and derivative works of the foregoing.",
        ],
      },
      {
        type: "p",
        text: "Except for the limited license expressly granted under these Terms, no right, title, license, ownership interest, or other interest in RealEsy Intellectual Property is transferred to Customer, any Authorized User, or any other person.",
      },
    ],
  },
  {
    id: "restrictions-on-use",
    title: "18. Restrictions on Use and Protection of RealEsy",
    blocks: [
      {
        type: "p",
        text: "Except where applicable law expressly prohibits such restriction, no Customer, Authorized User, or third party may, directly or indirectly:",
      },
      {
        type: "list",
        items: [
          "copy, reproduce, republish, distribute, sell, license, sublicense, rent, lease, or commercially exploit the Services or RealEsy Intellectual Property;",
          "modify, translate, adapt, or create derivative works based upon RealEsy Intellectual Property;",
          "reverse engineer, decompile, disassemble, decode, or otherwise attempt to derive source code, underlying ideas, algorithms, architecture, structure, or non-public APIs;",
          "copy, imitate, reconstruct, or misappropriate RealEsy’s proprietary interfaces, workflows, database structures, reports, templates, documentation, designs, processes, or other protected materials;",
          "use the Services or RealEsy Intellectual Property to develop, train, improve, benchmark, replicate, or assist in developing a competing product, software platform, service, or artificial intelligence system;",
          "scrape, crawl, harvest, extract, systematically download, index, or otherwise obtain data or content from the Services except through functionality expressly authorized by RealEsy;",
          "access or use non-public APIs without authorization;",
          "bypass, disable, defeat, circumvent, or interfere with authentication, security, licensing, usage limits, or technological protection measures;",
          "remove, conceal, or alter copyright, trademark, attribution, confidentiality, or proprietary-rights notices;",
          "access the Services for competitive analysis intended to reproduce or misappropriate proprietary functionality;",
          "disclose RealEsy Confidential Information to a competitor or other unauthorized party;",
          "allow unauthorized third parties to access the Services; or",
          "use RealEsy’s name, trademarks, branding, screenshots, documentation, or proprietary materials except as expressly authorized.",
        ],
      },
      {
        type: "p",
        text: "Unauthorized use of RealEsy Intellectual Property may result in immediate suspension or termination and may subject the responsible party to injunctive relief, damages, and other remedies available under applicable law.",
      },
    ],
  },
  {
    id: "feedback",
    title: "19. Feedback and Suggestions",
    blocks: [
      {
        type: "p",
        text: "If Customer or any Authorized User provides RealEsy with ideas, suggestions, recommendations, enhancement requests, feature requests, corrections, proposals, or other feedback (“Feedback”), Customer grants RealEsy a perpetual, irrevocable, worldwide, transferable, sublicensable, royalty-free right to use, reproduce, modify, commercialize, incorporate, distribute, and otherwise exploit such Feedback for any lawful purpose without restriction, attribution, compensation, or obligation.",
      },
      {
        type: "p",
        text: "Feedback does not include Customer Content or Customer Confidential Information merely because such information is communicated to RealEsy.",
      },
      {
        type: "p",
        text: "A Customer does not acquire ownership or exclusivity rights in functionality merely because Customer suggested, requested, funded, tested, or provided Feedback concerning such functionality, except where expressly agreed otherwise in a separate written agreement signed by RealEsy.",
      },
    ],
  },
  {
    id: "acceptable-use",
    title: "20. Acceptable Use",
    blocks: [
      { type: "p", text: "Users may not use the Services to:" },
      {
        type: "list",
        items: [
          "engage in unlawful, fraudulent, deceptive, abusive, or misleading activity;",
          "harass, threaten, intimidate, discriminate against, or impersonate another person or organization;",
          "transmit spam or unauthorized unsolicited communications;",
          "upload or distribute malware, ransomware, viruses, malicious code, or harmful software;",
          "gain or attempt unauthorized access to systems, Accounts, APIs, networks, or data;",
          "interfere with the security, integrity, availability, or performance of the Services;",
          "scrape, crawl, harvest, or extract data without authorization;",
          "use unauthorized bots, scripts, automation, or automated access mechanisms;",
          "circumvent access controls, usage restrictions, security mechanisms, or licensing restrictions;",
          "infringe copyrights, trademarks, patents, privacy rights, publicity rights, trade secrets, contractual rights, or other rights;",
          "upload unlawful, defamatory, deceptive, fraudulent, or infringing content;",
          "use the Services for unlawful surveillance or unauthorized collection of personal information;",
          "misuse AI functionality to facilitate unlawful, fraudulent, harmful, deceptive, or infringing activities;",
          "misrepresent the source, approval, or authenticity of information, proofs, Orders, or transactions; or",
          "encourage, facilitate, or assist another person in doing any of the foregoing.",
        ],
      },
      {
        type: "p",
        text: "RealEsy may investigate suspected violations and take reasonable protective action, including restricting content, suspending access, terminating Accounts, preserving relevant records, and cooperating with lawful governmental requests.",
      },
    ],
  },
  {
    id: "ai-features",
    title: "21. Artificial Intelligence Features",
    blocks: [
      {
        type: "p",
        text: "Certain Services may include artificial intelligence, machine-learning, automated assistance, or related functionality (“AI Features”).",
      },
      { type: "p", text: "AI Features may assist users with tasks such as:" },
      {
        type: "list",
        items: [
          "searching records;",
          "creating or assisting with Orders;",
          "generating or summarizing reports;",
          "answering questions;",
          "retrieving information; and",
          "improving workflow efficiency.",
        ],
      },
      {
        type: "p",
        text: "RealEsy may use third-party artificial intelligence providers, including OpenAI or other providers selected by RealEsy, to provide or support AI Features.",
      },
      {
        type: "p",
        text: "Customer acknowledges that information submitted to or processed through an AI Feature may be transmitted to and processed by such third-party providers as reasonably necessary to provide the requested functionality, subject to RealEsy’s Privacy Policy and applicable agreements with those providers.",
      },
      {
        type: "p",
        text: "RealEsy may change, add, or replace AI providers as the Services evolve.",
      },
      {
        type: "p",
        text: "AI-generated or AI-assisted output may be inaccurate, incomplete, outdated, misleading, unavailable, or otherwise unsuitable for a particular purpose.",
      },
      {
        type: "p",
        text: "CUSTOMER AND AUTHORIZED USERS ARE RESPONSIBLE FOR REVIEWING AND VERIFYING AI-GENERATED OR AI-ASSISTED OUTPUT BEFORE RELYING UPON IT OR USING IT TO TAKE BUSINESS, FINANCIAL, LEGAL, OPERATIONAL, CUSTOMER, OR OTHER ACTION.",
      },
      {
        type: "p",
        text: "AI Features do not constitute legal, tax, accounting, financial, real estate, professional, or other regulated advice.",
      },
      {
        type: "p",
        text: "To the maximum extent permitted by applicable law, RealEsy is not responsible for losses arising from a user’s failure to reasonably review or verify AI-generated information before acting upon it.",
      },
      {
        type: "p",
        text: "RealEsy may modify, restrict, impose usage limits upon, suspend, replace, or discontinue AI Features at any time.",
      },
      {
        type: "p",
        text: "The RealEsy Privacy Policy describes applicable processing and disclosure of personal information in connection with AI Features.",
      },
    ],
  },
  {
    id: "third-party-services",
    title: "22. Third-Party Services and Integrations",
    blocks: [
      {
        type: "p",
        text: "The Services may interoperate with or depend upon third-party products, APIs, applications, platforms, networks, carriers, payment processors, mapping providers, accounting providers, tax services, cloud infrastructure, database services, email providers, push-notification services, analytics providers, artificial intelligence providers, and other third-party services.",
      },
      {
        type: "p",
        text: "Third-party services are independently operated and may be governed by their own terms, privacy policies, availability commitments, and business practices.",
      },
      {
        type: "p",
        text: "RealEsy does not control and is not responsible for the acts, omissions, availability, accuracy, security, performance, pricing, policies, changes, suspension, termination, or discontinuation of independent third-party services.",
      },
      {
        type: "p",
        text: "A third party’s modification, restriction, suspension, pricing change, API change, or discontinuation of a product or service may cause related RealEsy functionality to change, become limited, require modification, or become unavailable.",
      },
      {
        type: "p",
        text: "RealEsy does not guarantee the continued availability of any particular third-party integration.",
      },
    ],
  },
  {
    id: "location-and-shipping",
    title: "23. Location, Mapping, Routing, Shipping and Carrier Services",
    blocks: [
      {
        type: "p",
        text: "Location, GPS, mapping, routing, navigation, address, shipping-rate, delivery-time, carrier, and similar information provided through the Services may depend upon Customer data, package information, device information, or independent third-party services.",
      },
      {
        type: "p",
        text: "Such information may be inaccurate, delayed, incomplete, estimated, changed by a third party, or unavailable.",
      },
      {
        type: "p",
        text: "Users are responsible for independently verifying material information including addresses and job locations, routes and navigation, package dimensions and weight, shipment contents and eligibility, carrier service level, shipping rates and estimates, delivery estimates, applicable surcharges and handling requirements, and other information material to a shipment or Service Order.",
      },
      {
        type: "p",
        text: "RealEsy is not a transportation carrier and does not guarantee pickup, transportation, delivery, carrier rates, transit times, or carrier performance.",
      },
      {
        type: "p",
        text: "Carriers may impose post-shipment adjustments, dimensional-weight adjustments, address-correction fees, residential or delivery-area surcharges, additional-handling charges, oversize charges, fuel surcharges, or other fees based on actual shipment characteristics or carrier rules.",
      },
      {
        type: "p",
        text: "The party responsible for the shipment is responsible for providing accurate shipment information and for applicable carrier charges and adjustments, except where a separate written agreement expressly provides otherwise.",
      },
      {
        type: "p",
        text: "Carrier claims, refunds, loss or damage claims, service-guarantee requests, and billing disputes are subject to the applicable carrier’s rules, procedures, deadlines, and contractual arrangements.",
      },
      {
        type: "p",
        text: "RealEsy is not responsible for lost or damaged shipments, carrier delays, delivery failures, rate changes, rate adjustments, carrier billing decisions, or other acts or omissions of carriers or logistics providers, except to the extent liability cannot lawfully be excluded.",
      },
    ],
  },
  {
    id: "electronic-communications",
    title: "24. Electronic Communications",
    blocks: [
      {
        type: "p",
        text: "By creating or maintaining an Account or using the Services, users consent to receive operational, transactional, security, and administrative communications electronically.",
      },
      { type: "p", text: "Such communications may include information relating to:" },
      {
        type: "list",
        items: [
          "Orders and Order status;",
          "quotes and approvals;",
          "invoices and payments;",
          "Service Orders and scheduling;",
          "proofs and completion records;",
          "Account verification;",
          "password resets and recovery;",
          "security alerts;",
          "maintenance and system notices;",
          "push notifications;",
          "changes affecting the Services or Account; and",
          "other operational or administrative matters.",
        ],
      },
      {
        type: "p",
        text: "Communications may be delivered through email, push notifications, in-app notifications, or other communication methods supported by RealEsy.",
      },
      {
        type: "p",
        text: "RealEsy does not currently provide SMS communications as part of the generally available launch Services.",
      },
      {
        type: "p",
        text: "If SMS functionality is introduced, additional terms, disclosures, consents, and communication preferences may apply.",
      },
      {
        type: "p",
        text: "Marketing or promotional communications, if offered, will be provided in accordance with applicable law and applicable communication preferences.",
      },
      {
        type: "p",
        text: "Users are responsible for maintaining accurate contact information.",
      },
    ],
  },
  {
    id: "platform-availability",
    title: "25. Platform Availability, Maintenance, Support and Changes",
    blocks: [
      {
        type: "p",
        text: "RealEsy will use commercially reasonable efforts to operate and maintain the Services but does not warrant uninterrupted, continuous, or error-free availability.",
      },
      {
        type: "p",
        text: "RealEsy may perform scheduled or emergency maintenance, upgrades, patches, security updates, infrastructure changes, migrations, or other work that temporarily affects availability.",
      },
      {
        type: "p",
        text: "Where commercially reasonable, RealEsy may provide advance notice of scheduled maintenance, but it is not obligated to provide advance notice for emergency maintenance or circumstances requiring prompt action.",
      },
      {
        type: "p",
        text: "RealEsy may modify, enhance, replace, add, restrict, or discontinue functionality as the platform evolves.",
      },
      {
        type: "p",
        text: "Unless expressly provided in a separate written Service Level Agreement (“SLA”), RealEsy does not guarantee any specific uptime percentage, response time, resolution time, recovery time, support response, or other service level.",
      },
      {
        type: "p",
        text: "Technical support may vary according to the applicable Subscription Plan, Add-On, order form, or separate support agreement.",
      },
      {
        type: "p",
        text: "Temporary interruptions may result from maintenance, software changes, infrastructure failures, internet outages, third-party services, security incidents, cyber events, force majeure events, or circumstances outside RealEsy’s reasonable control.",
      },
    ],
  },
  {
    id: "software-bugs-and-beta",
    title: "26. Software Bugs, Errors, Updates and Beta Features",
    blocks: [
      {
        type: "p",
        text: "Software is inherently complex and may contain bugs, defects, errors, incompatibilities, inaccuracies, or unexpected behavior.",
      },
      {
        type: "p",
        text: "RealEsy does not warrant that the Services will be completely error-free.",
      },
      {
        type: "p",
        text: "RealEsy may investigate and prioritize reported issues and use commercially reasonable efforts to address issues it determines require correction, but RealEsy does not guarantee that any particular defect will be corrected or corrected within a particular period unless expressly agreed in a written SLA.",
      },
      {
        type: "p",
        text: "RealEsy may release updates, patches, bug fixes, security improvements, infrastructure changes, and new functionality from time to time.",
      },
      {
        type: "p",
        text: "Features identified as beta, preview, experimental, early access, pilot, evaluation, or similar may be incomplete, contain additional errors, materially change, have limited availability, or be restricted, suspended, or discontinued at any time.",
      },
      {
        type: "p",
        text: "RealEsy has no obligation to implement any Customer feature request, enhancement request, suggestion, customization, or modification unless expressly agreed in writing.",
      },
    ],
  },
  {
    id: "suspension-and-termination",
    title: "27. Suspension, Termination and Data Following Termination",
    blocks: [
      {
        type: "p",
        text: "RealEsy may suspend, restrict, or terminate access to all or part of the Services where RealEsy reasonably determines that:",
      },
      {
        type: "list",
        items: [
          "these Terms have been violated;",
          "applicable fees remain unpaid;",
          "fraudulent, abusive, unlawful, deceptive, or suspicious activity has occurred;",
          "an Account creates a security, legal, operational, financial, or reputational risk;",
          "intellectual property rights have been infringed or are reasonably suspected to have been infringed;",
          "the Services are being misused;",
          "Customer or an Authorized User attempts unauthorized access or circumvention of platform restrictions;",
          "suspension is reasonably necessary to protect RealEsy, its users, systems, or third parties;",
          "continued access could expose RealEsy or another party to liability; or",
          "suspension or termination is required by law, court order, governmental request, or a third-party provider necessary to the Services.",
        ],
      },
      {
        type: "p",
        text: "Where circumstances reasonably permit, RealEsy may provide notice and an opportunity to cure a violation, but RealEsy is not required to do so where immediate action is reasonably necessary.",
      },
      {
        type: "p",
        text: "RealEsy may suspend an individual Authorized User without suspending the Customer’s entire organization.",
      },
      {
        type: "p",
        text: "Upon termination or expiration, Customer’s right to access and use the affected Services ends, subject to provisions that survive termination.",
      },
    ],
    subsections: [
      {
        title: "27.1 Customer Content Following Termination",
        blocks: [
          {
            type: "p",
            text: "Where export functionality is available, Customer is responsible for exporting Customer Content it wishes to retain before termination or expiration of its Account or Subscription Plan.",
          },
          {
            type: "p",
            text: "RealEsy may, but is not obligated to, provide a limited post-termination period during which Customer Content remains available for export.",
          },
          {
            type: "p",
            text: "After termination or expiration and any applicable export period, RealEsy may delete, anonymize, archive, or render inaccessible Customer Content, subject to applicable law, legitimate business requirements, contractual obligations, backup processes, fraud prevention, security requirements, tax and accounting requirements, dispute resolution, and the Privacy Policy.",
          },
          {
            type: "p",
            text: "RealEsy does not guarantee indefinite retention or recoverability of Customer Content after termination.",
          },
          {
            type: "p",
            text: "RealEsy may retain business records, financial records, audit logs, acceptance records, security logs, transaction records, communications, backups, and other information as required or permitted by applicable law and the Privacy Policy.",
          },
        ],
      },
      {
        title: "27.2 Account Deletion",
        blocks: [
          {
            type: "p",
            text: "Where required by applicable law or applicable mobile-platform requirements, RealEsy will provide a mechanism through which eligible users may request deletion of their Account.",
          },
          {
            type: "p",
            text: "Account deletion does not require RealEsy to delete information that RealEsy is required or permitted to retain for legitimate legal, security, fraud-prevention, accounting, tax, transaction-record, dispute-resolution, backup, or other lawful purposes.",
          },
          {
            type: "p",
            text: "Additional information concerning deletion and retention of personal information is provided in the RealEsy Privacy Policy.",
          },
        ],
      },
    ],
  },
  {
    id: "confidentiality",
    title: "28. Confidentiality",
    blocks: [
      {
        type: "p",
        text: "Each party may receive non-public information of the other party that reasonably should be understood to be confidential given its nature and the circumstances of disclosure (“Confidential Information”).",
      },
      {
        type: "p",
        text: "Customer Confidential Information may include non-public business information and Customer Content.",
      },
      {
        type: "p",
        text: "RealEsy Confidential Information includes, without limitation, non-public software information, product roadmaps, architecture, technical documentation, security information, non-public pricing, proprietary workflows, algorithms, source code, trade secrets, development plans, APIs, database structures, and non-public functionality.",
      },
      {
        type: "p",
        text: "The receiving party will use reasonable measures to protect Confidential Information and will use such information only as reasonably necessary to exercise its rights or perform its obligations in connection with the Services.",
      },
      {
        type: "p",
        text: "The receiving party may disclose Confidential Information to employees, contractors, professional advisers, and service providers who reasonably need the information and are subject to appropriate confidentiality obligations.",
      },
      {
        type: "p",
        text: "Confidential Information does not include information that the receiving party can demonstrate:",
      },
      {
        type: "list",
        items: [
          "is publicly available through no breach of obligation;",
          "was lawfully known without confidentiality obligation before disclosure;",
          "was lawfully received from a third party without confidentiality restriction; or",
          "was independently developed without use of the disclosing party’s Confidential Information.",
        ],
      },
      {
        type: "p",
        text: "A party may disclose Confidential Information where legally required, subject to applicable law and, where legally permitted, reasonable notice to the other party.",
      },
    ],
  },
  {
    id: "security",
    title: "29. Security and Security Incidents",
    blocks: [
      {
        type: "p",
        text: "RealEsy uses reasonable administrative, technical, and organizational measures designed to protect information processed through the Services against unauthorized access, use, alteration, loss, or disclosure.",
      },
      {
        type: "p",
        text: "No software platform, network, transmission method, or storage system can be guaranteed to be completely secure, and RealEsy does not warrant absolute security.",
      },
      {
        type: "p",
        text: "Customers remain responsible for maintaining appropriate security practices concerning their Accounts, credentials, devices, Authorized Users, permissions, and Customer systems.",
      },
      {
        type: "p",
        text: "If RealEsy confirms a security incident involving Customer information for which notification is required by applicable law, RealEsy will provide legally required notification to affected Customers or individuals in accordance with applicable law.",
      },
      {
        type: "p",
        text: "Additional information concerning RealEsy’s processing and protection of personal information is provided in the Privacy Policy.",
      },
    ],
  },
  {
    id: "disclaimer-of-warranties",
    title: "30. Disclaimer of Warranties",
    blocks: [
      {
        type: "p",
        text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICES, INCLUDING ALL SOFTWARE, CONTENT, AI FEATURES, REPORTS, DATA, INTEGRATIONS, MAPPING, SHIPPING, PAYMENT, TAX, COMMUNICATION, AND OTHER FUNCTIONALITY, ARE PROVIDED “AS IS” AND “AS AVAILABLE.”",
      },
      {
        type: "p",
        text: "REALESY DISCLAIMS ALL WARRANTIES, REPRESENTATIONS, CONDITIONS, AND GUARANTEES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, AVAILABILITY, AND QUIET ENJOYMENT.",
      },
      {
        type: "p",
        text: "RealEsy does not warrant that the Services will be uninterrupted, error-free, completely secure, free from harmful components, or suitable for every Customer’s particular business requirements.",
      },
      {
        type: "p",
        text: "RealEsy does not warrant the products, services, conduct, workmanship, representations, qualifications, licensing, availability, or performance of independent Vendors, Service Providers, Brokerages, agents, installers, End Customers, carriers, payment processors, AI providers, or other third parties.",
      },
      {
        type: "p",
        text: "RealEsy does not guarantee the accuracy of Customer-entered information, third-party information, AI-generated information, mapping information, shipping information, tax calculations, reports, estimates, or other information generated from data or services outside RealEsy’s exclusive control.",
      },
      {
        type: "p",
        text: "Nothing in these Terms excludes a warranty, right, or remedy that cannot lawfully be excluded.",
      },
    ],
  },
  {
    id: "indemnification",
    title: "31. Indemnification",
    blocks: [
      {
        type: "p",
        text: "To the maximum extent permitted by applicable law, Customer agrees to defend, indemnify, and hold harmless RealEsy, LLC and its affiliates, and their respective officers, directors, employees, contractors, representatives, successors, and assigns, from and against third-party claims, actions, proceedings, damages, liabilities, judgments, settlements, penalties, fines, costs, and reasonable attorneys’ fees arising out of or relating to:",
      },
      {
        type: "plainList",
        items: [
          "1. Customer’s or its Authorized Users’ use or misuse of the Services;",
          "2. Customer Content;",
          "3. Customer’s products or services;",
          "4. installation, removal, repair, maintenance, delivery, pickup, or other services performed by Customer or persons acting on Customer’s behalf;",
          "5. bodily injury, death, or property damage caused by Customer, its personnel, products, or services;",
          "6. Customer’s relationship or dispute with an End Customer, Vendor, Service Provider, Authorized User, or other third party;",
          "7. Customer’s violation of applicable law;",
          "8. Customer’s violation of these Terms;",
          "9. Customer’s infringement or alleged infringement of intellectual property, privacy, publicity, contractual, or other third-party rights;",
          "10. Customer’s pricing, taxes, refunds, chargebacks, warranties, representations, or payment terms; or",
          "11. acts or omissions of Customer’s employees, agents, installers, subcontractors, Service Providers, Vendors, or Authorized Users.",
        ],
      },
      {
        type: "p",
        text: "RealEsy reserves the right, at its own expense, to participate in the defense of any indemnified matter with counsel of its choosing.",
      },
      {
        type: "p",
        text: "Customer may not settle a claim in a manner that admits wrongdoing by, imposes obligations upon, restricts the rights of, or otherwise adversely affects RealEsy without RealEsy’s prior written consent.",
      },
    ],
  },
  {
    id: "limitation-of-liability",
    title: "32. Limitation of Liability",
    blocks: [
      {
        type: "p",
        text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, REALESY AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, CONTRACTORS, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, BUSINESS, GOODWILL, ANTICIPATED SAVINGS, BUSINESS OPPORTUNITY, OR DATA, ARISING OUT OF OR RELATING TO THE SERVICES OR THESE TERMS, REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
      },
      {
        type: "p",
        text: "Without limiting the foregoing, to the maximum extent permitted by applicable law, RealEsy is not responsible for losses arising from or relating to:",
      },
      {
        type: "list",
        items: [
          "product defects, safety, availability, or product quality;",
          "installation, removal, repair, maintenance, delivery, pickup, or other service errors;",
          "property damage, bodily injury, or other harm caused by independent Vendors, Service Providers, their personnel, or products or services they provide;",
          "shipping delays, lost or damaged shipments, carrier errors, carrier charges, or rate adjustments;",
          "Vendor, Brokerage, agent, installer, Service Provider, End Customer, or other user disputes;",
          "payment failures, processor holds, chargebacks, refunds, fraud, or processor actions;",
          "incorrect, incomplete, or unauthorized user-entered information;",
          "scheduling conflicts, delays, cancellations, or missed appointments;",
          "GPS, mapping, navigation, routing, location, or address inaccuracies;",
          "AI-generated or automated output;",
          "third-party services, APIs, providers, or integrations;",
          "interruptions, downtime, software bugs, defects, errors, data loss, or data corruption, except to the extent liability cannot lawfully be excluded or limited; or",
          "unauthorized access attributable to Customer’s failure to safeguard credentials, maintain reasonable security, revoke former users, or properly administer Authorized Users.",
        ],
      },
      {
        type: "p",
        text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, REALESY’S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THE SERVICES OR THESE TERMS WILL NOT EXCEED THE TOTAL AMOUNTS ACTUALLY PAID BY THE CUSTOMER TO REALESY DURING THE SIX (6) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM. IF THE CUSTOMER HAS PAID NO FEES TO REALESY DURING THAT PERIOD, REALESY’S AGGREGATE MONETARY LIABILITY WILL BE ZERO.",
      },
      {
        type: "p",
        text: "The limitations and exclusions in this Section apply to the maximum extent permitted by applicable law and do not exclude or limit liability to the extent such liability cannot lawfully be excluded or limited.",
      },
      {
        type: "p",
        text: "The limitations in this Section are an essential basis of the bargain between Customer and RealEsy and apply notwithstanding the failure of the essential purpose of any limited remedy, to the extent permitted by applicable law.",
      },
      {
        type: "p",
        text: "Nothing in this Section limits Customer’s obligation to pay amounts properly due to RealEsy or Customer’s indemnification obligations under these Terms.",
      },
    ],
  },
  {
    id: "privacy",
    title: "33. Privacy",
    blocks: [
      {
        type: "p",
        text: "RealEsy’s collection, use, processing, disclosure, and retention of personal information is described in the RealEsy Privacy Policy.",
      },
      {
        type: "p",
        text: "By using the Services, Customer acknowledges that it has been provided access to the Privacy Policy.",
      },
      {
        type: "p",
        text: "Customers that submit personal information concerning employees, agents, End Customers, installers, Service Providers, Vendors, or other individuals are responsible for providing any notices and obtaining any permissions, authorizations, or consents required by applicable law.",
      },
      {
        type: "p",
        text: "Customer may not use RealEsy to collect or process personal information unlawfully.",
      },
    ],
  },
  {
    id: "ip-complaints",
    title: "34. Copyright and Intellectual Property Complaints",
    blocks: [
      {
        type: "p",
        text: "RealEsy respects the intellectual property rights of others.",
      },
      {
        type: "p",
        text: "A rights holder who believes that Customer Content or other material made available through the Services infringes its intellectual property rights may contact RealEsy using the contact information provided in these Terms and provide sufficient information to identify the protected work or right, allegedly infringing material, location of the material, claimant and contact information, and basis for the claim.",
      },
      {
        type: "p",
        text: "RealEsy may investigate complaints, request additional information, remove or restrict access to allegedly infringing material, and suspend or terminate repeat or serious infringers where appropriate.",
      },
      {
        type: "p",
        text: "RealEsy maintains a policy of terminating, in appropriate circumstances, Accounts of users who repeatedly infringe the intellectual property rights of others.",
      },
      {
        type: "p",
        text: "Submission of an intellectual property complaint does not guarantee removal of material.",
      },
      {
        type: "p",
        text: "RealEsy reserves all rights, remedies, defenses, immunities, and safe-harbor protections available under applicable intellectual property law.",
      },
      {
        type: "p",
        text: "Any separate DMCA notice and counter-notice procedures adopted by RealEsy will be made available through the RealEsy website or other designated location.",
      },
    ],
  },
  {
    id: "dispute-resolution",
    title: "35. Governing Law, Arbitration and Dispute Resolution",
    blocks: [
      {
        type: "p",
        text: "PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS LEGAL RIGHTS, INCLUDING THE RIGHT TO HAVE CERTAIN DISPUTES DECIDED BY A COURT OR JURY AND THE ABILITY TO PARTICIPATE IN CLASS, COLLECTIVE, OR REPRESENTATIVE PROCEEDINGS.",
      },
      {
        type: "p",
        text: "These Terms and any dispute, claim, or controversy arising out of or relating to these Terms, the Services, or the relationship between Customer and RealEsy, LLC will be governed by and construed in accordance with the laws of the State of Georgia, without regard to conflict-of-laws principles, except to the extent otherwise required by applicable law.",
      },
    ],
    subsections: [
      {
        title: "35.1 Informal Dispute Resolution",
        blocks: [
          {
            type: "p",
            text: "Before initiating arbitration or litigation, the parties agree to make a reasonable good-faith effort to resolve the dispute informally.",
          },
          {
            type: "p",
            text: "A party asserting a dispute must provide written notice describing the nature of the dispute, relevant facts, and requested relief.",
          },
          {
            type: "p",
            text: "Any notice under this Section must be provided in accordance with Section 39.",
          },
          {
            type: "p",
            text: "Unless immediate equitable relief is reasonably necessary, the parties will have thirty (30) days following receipt of the notice to attempt to resolve the dispute before commencing arbitration or other formal proceedings.",
          },
        ],
      },
      {
        title: "35.2 Agreement to Arbitrate",
        blocks: [
          {
            type: "p",
            text: "Except for disputes expressly excluded in this Section, Customer and RealEsy, LLC agree that any dispute, claim, or controversy arising out of or relating to these Terms, the Services, or the relationship between Customer and RealEsy will be resolved through final and binding arbitration rather than in court.",
          },
          {
            type: "p",
            text: "The parties acknowledge that the Services and these Terms involve interstate commerce. The arbitration provisions of this Section will be governed by the Federal Arbitration Act, 9 U.S.C. §§ 1–16, to the maximum extent applicable.",
          },
          {
            type: "p",
            text: "Arbitration will be administered by the American Arbitration Association (“AAA”) under the AAA rules applicable to the dispute. For disputes arising primarily from a business or commercial relationship, the AAA Commercial Arbitration Rules will apply unless applicable law or mandatory AAA requirements require another AAA rule set.",
          },
          {
            type: "p",
            text: "Unless the parties agree otherwise in writing or applicable law requires otherwise, the legal seat and place of arbitration will be Fulton County, Georgia.",
          },
          {
            type: "p",
            text: "Proceedings may be conducted remotely by videoconference, telephone, written submissions, or other appropriate means where permitted by the applicable rules.",
          },
          {
            type: "p",
            text: "The arbitrator will have exclusive authority to resolve any dispute concerning the interpretation, applicability, enforceability, scope, or formation of this arbitration agreement, including any claim that all or part of this arbitration agreement is void or voidable. Notwithstanding the foregoing, a court of competent jurisdiction, and not an arbitrator, will determine the validity and enforceability of the Individual, Class, Collective and Representative Action Waiver in Section 35.3.",
          },
          {
            type: "p",
            text: "The arbitrator will have authority to resolve only the individual dispute submitted to arbitration and may award only relief that would otherwise be legally available to the individual claimant and that is consistent with these Terms.",
          },
          {
            type: "p",
            text: "The arbitrator must enforce the applicable limitations, exclusions, disclaimers, liability caps, contractual defenses, and other enforceable provisions contained in these Terms and may not award damages, remedies, or relief that these Terms validly exclude or prohibit.",
          },
          {
            type: "p",
            text: "Judgment upon an arbitration award may be entered by a court having jurisdiction.",
          },
          { type: "heading", text: "Arbitration Fees and Costs" },
          {
            type: "p",
            text: "Filing fees, administrative fees, hearing fees, arbitrator compensation, and other arbitration costs will be paid and allocated in accordance with the applicable AAA rules and applicable law.",
          },
          {
            type: "p",
            text: "RealEsy will pay or reimburse arbitration filing or administrative fees to the extent required by the applicable AAA rules or applicable law.",
          },
          {
            type: "p",
            text: "Except where applicable law, the applicable AAA rules, these Terms, or the arbitrator’s award provides otherwise, each party will bear its own attorneys’ fees, expert fees, and other costs incurred in connection with the arbitration.",
          },
          {
            type: "p",
            text: "Except as required by applicable AAA rules or applicable law, or as provided in a valid and binding arbitration award consistent with these Terms, nothing in these Terms requires RealEsy to pay or reimburse another party’s arbitration fees, attorneys’ fees, expert fees, or other costs.",
          },
        ],
      },
      {
        title: "35.3 Individual Arbitration; Class, Collective and Representative Action Waiver",
        blocks: [
          {
            type: "p",
            text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, CUSTOMER AND REALESY, LLC AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY.",
          },
          {
            type: "p",
            text: "NEITHER PARTY MAY BRING OR PARTICIPATE IN A CLASS ACTION, COLLECTIVE ACTION, REPRESENTATIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, CONSOLIDATED ACTION, OR OTHER PROCEEDING IN WHICH ONE PARTY SEEKS RELIEF ON BEHALF OF OTHER PERSONS OR ENTITIES.",
          },
          {
            type: "p",
            text: "Unless the parties expressly agree otherwise in writing, the arbitrator may not consolidate the claims of multiple parties or preside over any form of representative, class, collective, or consolidated proceeding.",
          },
          {
            type: "p",
            text: "If the Individual, Class, Collective and Representative Action Waiver in this Section is found invalid or unenforceable as to a particular claim or request for relief, that claim or request for relief will be severed from arbitration and may proceed only in a court of competent jurisdiction as provided in Section 35.9, while any remaining arbitrable claims will proceed in individual arbitration.",
          },
          {
            type: "p",
            text: "UNDER NO CIRCUMSTANCES WILL THE PARTIES BE REQUIRED TO ARBITRATE ANY CLASS, COLLECTIVE, REPRESENTATIVE, OR CONSOLIDATED CLAIM, AND THE ARBITRATOR WILL HAVE NO AUTHORITY TO PRESIDE OVER SUCH A PROCEEDING WITHOUT THE EXPRESS WRITTEN AGREEMENT OF BOTH PARTIES.",
          },
        ],
      },
      {
        title: "35.4 Jury-Trial Waiver",
        blocks: [
          {
            type: "p",
            text: "TO THE EXTENT ANY DISPUTE IS PERMITTED TO PROCEED IN COURT RATHER THAN ARBITRATION, CUSTOMER AND REALESY, LLC KNOWINGLY AND VOLUNTARILY WAIVE, TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ANY RIGHT TO A TRIAL BY JURY.",
          },
        ],
      },
      {
        title: "35.5 Small Claims Court",
        blocks: [
          {
            type: "p",
            text: "Either party may bring an individual claim in a small claims court of competent jurisdiction if the claim qualifies for that court and remains solely on an individual basis.",
          },
        ],
      },
      {
        title: "35.6 Intellectual Property, Confidentiality and Security Relief",
        blocks: [
          {
            type: "p",
            text: "Notwithstanding the arbitration requirement, RealEsy, LLC may seek temporary, preliminary, permanent, injunctive, equitable, or other appropriate relief from a court of competent jurisdiction where reasonably necessary to prevent, stop, or remedy actual or threatened:",
          },
          {
            type: "list",
            items: [
              "infringement, misuse, or misappropriation of RealEsy Intellectual Property;",
              "unauthorized disclosure or misuse of RealEsy Confidential Information or trade secrets;",
              "reverse engineering, scraping, unauthorized extraction, decompilation, disassembly, or copying of RealEsy technology;",
              "unauthorized access to or interference with RealEsy systems, Accounts, networks, APIs, security mechanisms, or data; or",
              "other conduct reasonably likely to cause immediate or irreparable harm for which monetary damages alone may be inadequate.",
            ],
          },
          {
            type: "p",
            text: "Seeking such relief does not waive the right to arbitrate other claims.",
          },
        ],
      },
      {
        title: "35.7 Arbitration Opt-Out",
        blocks: [
          {
            type: "p",
            text: "A Customer who does not wish to be bound by the arbitration provisions of this Section may opt out by providing written notice to RealEsy within thirty (30) days after first accepting the version of these Terms containing the arbitration provision.",
          },
          {
            type: "p",
            text: "The opt-out notice must be provided in accordance with Section 39, must clearly state that Customer is opting out of the arbitration agreement, and must contain sufficient information to identify the applicable Customer and Account.",
          },
          {
            type: "p",
            text: "An effective opt-out applies only to the arbitration provisions and does not affect the remaining Terms.",
          },
          {
            type: "p",
            text: "RealEsy will not terminate or restrict Customer’s Account solely because Customer timely exercises the right to opt out of arbitration.",
          },
        ],
      },
      {
        title: "35.8 Mass Arbitration",
        blocks: [
          {
            type: "p",
            text: "If a number of substantially similar arbitration demands sufficient to qualify as a Mass Arbitration under the then-applicable AAA Mass Arbitration Supplementary Rules are filed against or on behalf of RealEsy or related parties and involve consistent or coordinated representation, the disputes may be administered in accordance with the then-applicable AAA Mass Arbitration Supplementary Rules.",
          },
          {
            type: "p",
            text: "The parties agree to cooperate in good faith with AAA and any appointed process arbitrator regarding efficient administration, sequencing, mediation, batching, bellwether proceedings, or other procedures authorized by the applicable AAA rules.",
          },
          {
            type: "p",
            text: "Nothing in this subsection permits class, collective, representative, or consolidated adjudication of the merits of claims where such proceedings are otherwise waived under these Terms.",
          },
        ],
      },
      {
        title: "35.9 Court Venue for Non-Arbitrable Matters",
        blocks: [
          {
            type: "p",
            text: "Any dispute that is not subject to arbitration and is properly brought in court must, except where applicable law requires otherwise, be brought exclusively in a court of competent jurisdiction located in Fulton County, Georgia, or in the applicable United States federal court having jurisdiction over Fulton County, Georgia.",
          },
          {
            type: "p",
            text: "Each party consents to the personal jurisdiction and venue of those courts.",
          },
        ],
      },
      {
        title: "35.10 Confidentiality of Arbitration",
        blocks: [
          {
            type: "p",
            text: "To the extent permitted by applicable law and the applicable AAA rules, the parties will use reasonable efforts to maintain the confidentiality of non-public documents, testimony, evidence, filings, and information exchanged solely in connection with an arbitration.",
          },
          {
            type: "p",
            text: "This provision does not prohibit disclosure reasonably necessary to pursue or defend the arbitration; obtain professional legal, accounting, insurance, or other advice; enforce, confirm, modify, or challenge an arbitration award; comply with applicable law, court order, subpoena, regulatory requirement, or governmental request; or protect or enforce a party’s legal rights.",
          },
          {
            type: "p",
            text: "Nothing in this Section requires a court filing or arbitration award to remain confidential where confidentiality is not legally available.",
          },
        ],
      },
    ],
  },
  {
    id: "limitation-period",
    title: "36. Limitation Period for Claims",
    blocks: [
      {
        type: "p",
        text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ANY CLAIM, ACTION, ARBITRATION, OR PROCEEDING ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES MUST BE COMMENCED WITHIN ONE (1) YEAR AFTER THE CLAIM ACCRUES.",
      },
      {
        type: "p",
        text: "ANY CLAIM NOT COMMENCED WITHIN THAT PERIOD WILL BE PERMANENTLY BARRED, EXCEPT TO THE EXTENT APPLICABLE LAW PROHIBITS THE PARTIES FROM CONTRACTUALLY LIMITING THE TIME IN WHICH THE PARTICULAR CLAIM MAY BE BROUGHT.",
      },
      { type: "p", text: "The one-year limitation in this Section does not apply to:" },
      {
        type: "plainList",
        items: [
          "1. RealEsy’s claims to recover unpaid Subscription Plan fees, Add-On fees, custom-development fees, professional-service fees, reimbursements, carrier charges, or other amounts properly owed to RealEsy; or",
          "2. claims for temporary, preliminary, permanent, injunctive, equitable, or other relief described in Section 35.6.",
        ],
      },
      {
        type: "p",
        text: "Nothing in this Section extends a limitations period that applicable law requires to be shorter.",
      },
    ],
  },
  {
    id: "force-majeure",
    title: "37. Force Majeure",
    blocks: [
      {
        type: "p",
        text: "RealEsy will not be liable for delay, interruption, failure, or inability to perform caused by circumstances beyond its reasonable control, including natural disasters, severe weather, fire, flood, epidemic or pandemic, war, terrorism, civil unrest, labor disputes, governmental actions, power failures, telecommunications failures, internet disruptions, widespread cyberattacks not caused by RealEsy’s failure to exercise legally required care, cloud infrastructure failures, third-party service outages, carrier failures, or similar events beyond RealEsy’s reasonable control.",
      },
      {
        type: "p",
        text: "A force majeure event does not excuse Customer from paying fees or other amounts that became due before or during the force majeure event for Services already provided or obligations otherwise properly chargeable under an applicable agreement.",
      },
    ],
  },
  {
    id: "changes",
    title: "38. Changes to the Services and Terms",
    blocks: [
      { type: "p", text: "RealEsy may update these Terms from time to time." },
      {
        type: "p",
        text: "When changes are material, RealEsy will provide notice as required by applicable law, which may include notice through the Services, email, or publication of updated Terms.",
      },
      { type: "p", text: "Updated Terms will identify their effective date." },
      {
        type: "p",
        text: "Where applicable law or the nature of a material change requires affirmative consent, RealEsy may require Customer or Authorized Users to accept the updated Terms before continuing to use the Services.",
      },
      {
        type: "p",
        text: "Continued use alone will not be treated as a substitute for affirmative acceptance where applicable law requires affirmative consent.",
      },
      {
        type: "p",
        text: "A material modification to the arbitration provisions will be handled in accordance with applicable law and will not retroactively alter the treatment of a dispute that arose before the modification to the extent prohibited by applicable law.",
      },
    ],
  },
  {
    id: "legal-notices",
    title: "39. Legal Notices",
    blocks: [
      {
        type: "p",
        text: "Notices relating to ordinary operation of the Services may be provided electronically through the Services or using Customer’s Account contact information.",
      },
      {
        type: "p",
        text: "Formal contractual or legal notices to RealEsy under these Terms must be sent to the contact information designated in Section 48 or to any replacement legal-notice address subsequently designated by RealEsy.",
      },
      {
        type: "p",
        text: "RealEsy may provide formal notices to Customer using the email address, physical address, Account notification system, or other contact information associated with Customer’s Account.",
      },
      {
        type: "p",
        text: "Customer is responsible for maintaining accurate and current contact information.",
      },
      {
        type: "p",
        text: "Electronic notices will be deemed received when sent or made available through the applicable Account, except where applicable law requires another method of delivery.",
      },
      {
        type: "p",
        text: "Nothing in this Section alters any service-of-process requirements imposed by applicable law.",
      },
    ],
  },
  {
    id: "apple-app-store",
    title: "40. Apple App Store Terms",
    blocks: [
      {
        type: "p",
        text: "The following provisions apply solely to any version of the RealEsy mobile application obtained through Apple’s App Store (“iOS Application”).",
      },
    ],
    subsections: [
      {
        title: "40.1 Agreement Between Customer and RealEsy",
        blocks: [
          {
            type: "p",
            text: "Customer acknowledges and agrees that these Terms are entered into between Customer and RealEsy, LLC only, and not with Apple Inc. (“Apple”).",
          },
          {
            type: "p",
            text: "RealEsy, and not Apple, is solely responsible for the iOS Application and its content, subject to these Terms.",
          },
        ],
      },
      {
        title: "40.2 Scope of License",
        blocks: [
          {
            type: "p",
            text: "The license granted to Customer for the iOS Application is limited to a non-transferable license to use the iOS Application on Apple-branded products that Customer owns or controls and as permitted by the applicable usage rules contained in the Apple Media Services Terms and Conditions, including applicable permitted Family Sharing or volume-purchasing rights.",
          },
        ],
      },
      {
        title: "40.3 Maintenance and Support",
        blocks: [
          {
            type: "p",
            text: "RealEsy, and not Apple, is responsible for providing maintenance and support services with respect to the iOS Application to the extent required under these Terms or applicable law.",
          },
          {
            type: "p",
            text: "Apple has no obligation to furnish maintenance or support services for the iOS Application.",
          },
        ],
      },
      {
        title: "40.4 Warranties",
        blocks: [
          {
            type: "p",
            text: "To the extent any warranty concerning the iOS Application cannot lawfully be disclaimed, RealEsy is solely responsible for such warranty.",
          },
          {
            type: "p",
            text: "If the iOS Application fails to conform to an applicable warranty, Customer may notify Apple, and Apple may refund any purchase price paid to Apple for the iOS Application, if applicable.",
          },
          {
            type: "p",
            text: "To the maximum extent permitted by applicable law, Apple has no other warranty obligation with respect to the iOS Application.",
          },
        ],
      },
      {
        title: "40.5 Product Claims",
        blocks: [
          {
            type: "p",
            text: "RealEsy, and not Apple, is responsible for addressing claims relating to the iOS Application or Customer’s possession or use of the iOS Application, including:",
          },
          {
            type: "list",
            items: [
              "product-liability claims;",
              "claims that the iOS Application fails to conform to applicable legal or regulatory requirements; and",
              "claims arising under consumer-protection, privacy, or similar laws,",
            ],
          },
          { type: "p", text: "in each case subject to these Terms and applicable law." },
        ],
      },
      {
        title: "40.6 Intellectual Property Claims",
        blocks: [
          {
            type: "p",
            text: "If a third party claims that the iOS Application or Customer’s possession or use of the iOS Application infringes that third party’s intellectual property rights, RealEsy, and not Apple, will be responsible for the investigation, defense, settlement, and discharge of such claim to the extent required by applicable law.",
          },
        ],
      },
      {
        title: "40.7 Legal Compliance",
        blocks: [
          { type: "p", text: "Customer represents and warrants that Customer:" },
          {
            type: "plainList",
            items: [
              "1. is not located in a country or region subject to a United States Government embargo or designated by the United States Government as supporting terrorism; and",
              "2. is not listed on any United States Government list of prohibited or restricted parties.",
            ],
          },
        ],
      },
      {
        title: "40.8 Third-Party Terms",
        blocks: [
          {
            type: "p",
            text: "Customer must comply with applicable third-party terms when using the iOS Application, including applicable wireless-data, device, network, and Apple service terms.",
          },
        ],
      },
      {
        title: "40.9 Apple as Third-Party Beneficiary",
        blocks: [
          {
            type: "p",
            text: "Customer and RealEsy acknowledge and agree that Apple and Apple’s subsidiaries are third-party beneficiaries of this Section and these Terms as they relate to the iOS Application.",
          },
          {
            type: "p",
            text: "Upon Customer’s acceptance of these Terms, Apple will have the right, and will be deemed to have accepted the right, to enforce the applicable provisions of these Terms against Customer as a third-party beneficiary.",
          },
        ],
      },
    ],
  },
  {
    id: "assignment",
    title: "41. Assignment",
    blocks: [
      {
        type: "p",
        text: "Customer may not assign, delegate, or transfer these Terms or its rights or obligations under these Terms without RealEsy’s prior written consent.",
      },
      {
        type: "p",
        text: "RealEsy may assign or transfer these Terms in connection with a merger, acquisition, corporate reorganization, financing, sale of assets, change of control, or transfer of the RealEsy business, subject to applicable law.",
      },
      {
        type: "p",
        text: "Any prohibited assignment is void to the extent permitted by law.",
      },
    ],
  },
  {
    id: "severability",
    title: "42. Severability",
    blocks: [
      {
        type: "p",
        text: "If any provision of these Terms is held invalid, illegal, or unenforceable, that provision will be interpreted, modified, or enforced to the maximum extent permitted by law consistent with its intended purpose, and the remaining provisions will remain in full force and effect.",
      },
    ],
  },
  {
    id: "waiver",
    title: "43. Waiver",
    blocks: [
      {
        type: "p",
        text: "A party’s failure or delay in exercising a right or remedy under these Terms does not waive that right or remedy.",
      },
      {
        type: "p",
        text: "A waiver is effective only with respect to the specific matter for which it is given and will not constitute a continuing waiver unless expressly stated.",
      },
    ],
  },
  {
    id: "entire-agreement",
    title: "44. Entire Agreement and Order of Precedence",
    blocks: [
      {
        type: "p",
        text: "These Terms, the Privacy Policy, applicable order forms, Subscription Plan terms, Add-On terms, statements of work, and any other written agreement expressly incorporated by reference constitute the agreement between RealEsy and Customer concerning the Services covered by those documents.",
      },
      {
        type: "p",
        text: "If a separately executed written agreement between RealEsy and Customer expressly conflicts with these Terms, the separately executed written agreement will control to the extent of the conflict.",
      },
      {
        type: "p",
        text: "No purchase order, vendor form, Customer policy, or other Customer-generated document will modify these Terms merely because RealEsy receives, processes, or references that document, unless RealEsy expressly agrees to the modification in a writing signed by an authorized RealEsy representative.",
      },
    ],
  },
  {
    id: "no-third-party-beneficiaries",
    title: "45. No Third-Party Beneficiaries",
    blocks: [
      {
        type: "p",
        text: "Except for Apple Inc. and its subsidiaries solely to the extent expressly provided in Section 40, and except as otherwise expressly provided in these Terms, these Terms do not create rights enforceable by any person or entity other than RealEsy and Customer.",
      },
      {
        type: "p",
        text: "An End Customer, Vendor, Service Provider, Authorized User, carrier, payment processor, or other third party does not become a third-party beneficiary of these Terms solely because the Services are used in connection with a transaction involving that party.",
      },
    ],
  },
  {
    id: "survival",
    title: "46. Survival",
    blocks: [
      {
        type: "p",
        text: "Termination, cancellation, suspension, or expiration of these Terms or Customer’s access to the Services will not affect rights, obligations, liabilities, or remedies that accrued before such termination, cancellation, suspension, or expiration.",
      },
      {
        type: "p",
        text: "Any provisions of these Terms that by their nature are intended to survive will remain in effect, including provisions concerning:",
      },
      {
        type: "list",
        items: [
          "accrued payment obligations;",
          "RealEsy Intellectual Property;",
          "restrictions on use of the Services;",
          "Customer Content licenses to the extent necessary for lawful retention, backup, compliance, or wind-down;",
          "aggregated and de-identified information;",
          "Feedback;",
          "confidentiality;",
          "disclaimers of warranties;",
          "indemnification;",
          "limitation of liability;",
          "arbitration and dispute resolution;",
          "limitation periods;",
          "governing law; and",
          "other provisions necessary to give effect to the foregoing.",
        ],
      },
      {
        type: "p",
        text: "For clarity, termination or cancellation of an Account does not grant any former Customer or Authorized User the right to copy, reproduce, reverse engineer, disclose, exploit, use to compete with, or otherwise misuse RealEsy Intellectual Property or Confidential Information in a manner prohibited by these Terms.",
      },
    ],
  },
  {
    id: "headings-and-interpretation",
    title: "47. Headings and Interpretation",
    blocks: [
      {
        type: "p",
        text: "Section headings are provided for convenience and do not limit or affect interpretation of these Terms.",
      },
      {
        type: "p",
        text: "The words “including,” “includes,” and similar terms mean “including without limitation” unless the context clearly requires otherwise.",
      },
      {
        type: "p",
        text: "References to the singular include the plural and vice versa where appropriate.",
      },
      {
        type: "p",
        text: "These Terms will not be interpreted against a party solely because that party participated in drafting them.",
      },
    ],
  },
  {
    id: "contact-information",
    title: "48. Contact Information",
    blocks: [
      { type: "p", text: "Questions regarding these Terms may be directed to:" },
      {
        type: "address",
        lines: [
          "RealEsy, LLC",
          "2397 Church Rd",
          "Atlanta, GA 30339, USA",
          "Email: help@realesy.com",
          "Website: www.realesy.com",
        ],
      },
      {
        type: "p",
        text: "Formal arbitration opt-out notices and contractual notices may be sent using the contact information above unless RealEsy designates another address or electronic method for such notices.",
      },
      {
        type: "p",
        text: "RealEsy may update its contact or legal-notice information by updating this Section or providing notice through the Services.",
      },
    ],
  },
];

const isAllCaps = (text: string) => /[A-Z]/.test(text) && text === text.toUpperCase();

const renderAddressLine = (line: string) => {
  if (line.startsWith("Email: ")) {
    const email = line.replace("Email: ", "");
    return (
      <>
        Email:{" "}
        <a href={`mailto:${email}`} className="text-blue-600 hover:text-blue-700 hover:underline">
          {email}
        </a>
      </>
    );
  }
  if (line.startsWith("Website: ")) {
    const site = line.replace("Website: ", "");
    return (
      <>
        Website:{" "}
        <a
          href={`https://${site}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-700 hover:underline"
        >
          {site}
        </a>
      </>
    );
  }
  return line;
};

const renderBlock = (block: Block, key: number) => {
  switch (block.type) {
    case "p":
      return (
        <p
          key={key}
          className={`text-base md:text-lg leading-relaxed ${
            isAllCaps(block.text) ? "text-gray-900 font-semibold" : "text-gray-700"
          }`}
        >
          {block.lead && <span className="font-semibold text-gray-900">{block.lead} </span>}
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul key={key} className="list-disc pl-6 space-y-1.5 text-gray-700 text-base md:text-lg leading-relaxed marker:text-blue-600">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case "plainList":
      return (
        <ul key={key} className="space-y-3 pl-2 text-gray-700 text-base md:text-lg leading-relaxed">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case "heading":
      return (
        <h4 key={key} className="font-semibold text-lg text-gray-900 pt-2">
          {block.text}
        </h4>
      );
    case "address":
      return (
        <div
          key={key}
          className="bg-blue-50 border-l-4 border-blue-600 rounded-r-lg p-6 text-gray-800 text-base md:text-lg leading-relaxed"
        >
          {block.lines.map((line) => (
            <div key={line}>{renderAddressLine(line)}</div>
          ))}
        </div>
      );
    default:
      return null;
  }
};

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl text-white">{TITLE}</h1>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <article className="max-w-4xl mx-auto space-y-12">
            <div className="space-y-5">{intro.map((text, i) => renderBlock({ type: "p", text }, i))}</div>

            {sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-28">
                <div className="border-t border-gray-200 pt-10">
                  <h2 className="font-medium text-2xl text-gray-900 mb-6">{section.title}</h2>
                  <div className="space-y-5">
                    {section.blocks.map((block, i) => renderBlock(block, i))}
                  </div>

                  {section.subsections?.map((sub) => (
                    <div key={sub.title} className="mt-8">
                      <h3 className="font-medium text-xl text-gray-900 mb-4">{sub.title}</h3>
                      <div className="space-y-5">
                        {sub.blocks.map((block, i) => renderBlock(block, i))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </article>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
